import { Component, Element, Host, Prop, Watch, h } from '@stencil/core';

import { renderTools } from '../../helpers/RenderTools';
import { articleHasEnergyLabel } from '../../helpers/helpers';
import type {
  CategoryTeaser,
  CssClassMap,
  Product,
  StaticTeasers,
} from '../../helpers/utils/interfaces';

@Component({
  tag: 'cms-project-module',
  styleUrl: 'cms-project-module.scss',
  shadow: true,
})
export class CmsProjectModule {
  private readonly teasers = {
    firstTeaserResult: null,
    secondTeaserResult: null,
    thirdTeaserResult: null,
    fourthTeaserResult: null,
    fifthTeaserResult: null,
  };

  private readonly imageSizes = `
(min-width: 768px) and (max-width: 1279px) and (min-resolution: 288dpi) 30vw,
(min-width: 768px) and (max-width: 1279px) and (min-resolution: 192dpi) 30vw,                     
(min-width: 768px) and (max-width: 1279px) and (min-resolution: 96dpi) 30vw,
(min-width: 1280px) 392px,
 100vw`;

  private readonly firstTeaserImageSizes = `
 (min-width: 768px) and (max-width: 1279px) and (min-resolution: 288dpi) 30vw,
 (min-width: 768px) and (max-width: 1279px) and (min-resolution: 192dpi) 30vw,
 (min-width: 768px) and (max-width: 1279px) and (min-resolution: 96dpi) 60vw,
 (min-width: 1280px) 796px,
  100vw`;

  private bgTheme: string;

  @Prop() useProductCard = false;

  /**
   * set the component headline
   */
  @Prop() headline = '';

  /**
   * set the component background
   */
  @Prop({ mutable: true }) background = '';

  /**
   * set first teaser content
   */
  @Prop({ mutable: true }) firstTeaser: StaticTeasers | string;

  /**
   * set second teaser content
   */
  @Prop() secondTeaser: Product[] | string;

  /**
   * set third teaser content
   */
  @Prop() thirdTeaser: CategoryTeaser[] | string;

  /**
   * set fourth teaser content
   */
  @Prop() fourthTeaser: StaticTeasers | string;

  /**
   * set fifth teaser content
   */
  @Prop({ mutable: true }) fifthTeaser: StaticTeasers | string;
  /**
   * Set the title which is used in the ratings
   */
  @Prop() ratingTitle = '';
  /**
   * set accessible-name for the energy-label datasheet-link
   */
  @Prop() datasheetAccessibleName?: string;

  @Element() hostElement: HTMLElement;

  private priceSize = 'LG';

  @Watch('firstTeaser')
  private firstTeaserWatcher(newValue: string | StaticTeasers) {
    if (newValue) {
      const firstTeaser =
        typeof newValue === 'string' ? JSON.parse(newValue) : newValue;
      this.teasers.firstTeaserResult = this.addPropsToTeaserResult(
        firstTeaser[0],
        {
          imageSizes: this.firstTeaserImageSizes,
          background: this.bgTheme,
        },
      );
    }
  }

  @Watch('secondTeaser')
  private secondTeaserWatcher(newValue: string | Product[]) {
    if (newValue) {
      const secondTeaser =
        typeof newValue === 'string' ? JSON.parse(newValue) : newValue;
      this.teasers.secondTeaserResult = secondTeaser[0];
      this.priceSize = articleHasEnergyLabel(this.teasers.secondTeaserResult)
        ? 'MD'
        : 'LG';
    }
  }

  @Watch('thirdTeaser')
  private thirdTeaserWatcher(newValue) {
    if (newValue) {
      const thirdTeaser =
        typeof newValue === 'string' ? JSON.parse(newValue) : newValue;
      this.teasers.thirdTeaserResult = thirdTeaser[0];
    }
  }

  @Watch('fourthTeaser')
  private fourthTeaserWatcher(newValue: string | StaticTeasers) {
    if (newValue) {
      const fourthTeaser =
        typeof newValue === 'string' ? JSON.parse(newValue) : newValue;
      this.teasers.fourthTeaserResult = this.addPropsToTeaserResult(
        fourthTeaser[0],
        {
          imageSizes: this.imageSizes,
          background: this.bgTheme,
        },
      );
    }
  }

  @Watch('fifthTeaser')
  private fifthTeaserWatcher(newValue: string | StaticTeasers) {
    if (newValue) {
      const fifthTeaser =
        typeof newValue === 'string' ? JSON.parse(newValue) : newValue;
      this.teasers.fifthTeaserResult = this.addPropsToTeaserResult(
        fifthTeaser[0],
        {
          imageSizes: this.imageSizes,
          background: this.bgTheme,
        },
      );
    }
  }

  private wrapperClassMap(): CssClassMap {
    return {
      'container-wrapper-inner': true,
      'static-teaser': true,
    };
  }

  private addPropsToTeaserResult(
    teaser: StaticTeasers | Product,
    props: object,
  ) {
    return {
      ...teaser,
      ...props,
    };
  }

  componentWillLoad() {
    if (this.background !== 'no_background' && this.background !== '') {
      this.bgTheme = renderTools.getBgTheme(this.background);
      this.hostElement.classList.add('has-bg');
      if (this.bgTheme) {
        this.hostElement.classList.add(this.bgTheme);
      }
    }

    if (this.firstTeaser) {
      this.firstTeaserWatcher(this.firstTeaser);
    }

    if (this.secondTeaser) {
      this.secondTeaserWatcher(this.secondTeaser);
    }

    if (this.thirdTeaser) {
      this.thirdTeaserWatcher(this.thirdTeaser);
    }

    if (this.fourthTeaser) {
      this.fourthTeaserWatcher(this.fourthTeaser);
    }

    if (this.fifthTeaser) {
      this.fifthTeaserWatcher(this.fifthTeaser);
    }
  }

  render() {
    return (
      <Host
        style={
          this.background !== undefined
            ? {
                padding: this.background === 'no_background' ? '' : '2rem 0',
                backgroundImage: `url(${renderTools.getBgImageSrc(
                  this.background,
                )})`,
                backgroundRepeat: 'repeat',
              }
            : null
        }
      >
        <div class={this.wrapperClassMap()}>
          <cms-headline
            headlineText={this.headline}
            headlineTag={'h2'}
            headlinePosition='center'
            headlineClass={`headline ${this.bgTheme ?? ''} `}
            isAdaptive={true}
          />
          <ul class={'teaser-content project-module'}>
            {this.teasers.firstTeaserResult === null
              ? null
              : renderTools.getTeaserHtml(this.teasers.firstTeaserResult)}
            {this.teasers.secondTeaserResult === null ? null : (
              <li class={'teaser-content-inner article'}>
                <cms-product-card
                  article={this.teasers.secondTeaserResult}
                  ratingTitle={this.ratingTitle}
                  priceSize={this.priceSize}
                  ctaLabel={this.teasers.secondTeaserResult.ctaLabel}
                  maxNameLength={40}
                  datasheetAccessibleName={this.datasheetAccessibleName}
                />
              </li>
            )}
            {this.teasers.thirdTeaserResult === null
              ? null
              : renderTools.renderCategoryTeaser(
                  this.teasers.thirdTeaserResult,
                )}
            {this.teasers.fourthTeaserResult === null
              ? null
              : renderTools.getTeaserHtml(this.teasers.fourthTeaserResult)}
            {this.teasers.fifthTeaserResult === null
              ? null
              : renderTools.getTeaserHtml(this.teasers.fifthTeaserResult)}
          </ul>
        </div>
      </Host>
    );
  }
}
